<template>
  <div>
    <WdsRadioGroup
      v-model:checked="employmentCost"
      label="Who is responsible for paying relevant taxes?"
      name="employment_cost"
    >
      <WdsRadio :disabled="!canUpdate" value="unknown">Not sure</WdsRadio>
      <WdsRadio :disabled="!canUpdate" value="ontop">
        We will pay additional taxes on top of the freelancer's bills
      </WdsRadio>
      <WdsRadio :disabled="!canUpdate" value="incl">
        We expect taxes to be paid by the freelancer themselves out of the already billed amount
      </WdsRadio>
    </WdsRadioGroup>

    <WdsAlert v-if="!canUpdate" class="tw-mt-4" icon warning>
      Your employment cost preference cannot be updated
      <template #description>
        You have one or more hires that are paid through a PAYE scheme, are active and that have
        approved bills.
      </template>
    </WdsAlert>

    <WdsAlert
      v-if="errorMessage && showError"
      class="tw-mt-2"
      closable
      error
      @close="showError = false"
    >
      {{ $t('An error occurred') }}
      <template #description>
        {{ errorMessage }}
      </template>
    </WdsAlert>
    <WdsAlert v-else-if="showSuccess" class="tw-mt-2" closable success @close="showSuccess = false">
      {{ $t('Thanks, your settings has been updated') }}
    </WdsAlert>

    <div class="tw-mt-4">
      <WdsButton :disabled="loading || !canUpdate" primary @click="onSave">
        <div class="tw-flex tw-items-center">
          <span>Save</span>
          <WdsSpinner v-show="loading" class="tw-ml-2" indeterminate>
            <WdsIcon name="bam" :size="16" />
          </WdsSpinner>
        </div>
      </WdsButton>
    </div>
  </div>
</template>

<script>
import { reactive, ref } from 'vue'
import {
  WdsAlert,
  WdsButton,
  WdsIcon,
  WdsRadio,
  WdsRadioGroup,
  WdsSpinner,
} from '@worksome/wds-components'
import { UpdateComplianceMutation } from '@/components/compliances/mutations/UpdateComplianceMutation'
import { FormValidation } from '@/utils/formValidation'

export default {
  components: {
    WdsAlert,
    WdsButton,
    WdsIcon,
    WdsRadio,
    WdsRadioGroup,
    WdsSpinner,
  },
  props: {
    company: {
      type: Object,
      required: true,
    },
    complianceData: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const errors = ref([])
    const internalComplianceData = reactive(props.complianceData)
    const loading = ref(false)
    const showError = ref(false)
    const showSuccess = ref(false)

    return {
      errors,
      internalComplianceData,
      loading,
      showError,
      showSuccess,
    }
  },
  computed: {
    canUpdate() {
      return this.internalComplianceData.canUpdate ?? false
    },
    employmentCost: {
      get() {
        return this.internalComplianceData.employmentCost ?? 'unknown'
      },
      set(employmentCost) {
        this.internalComplianceData.employmentCost = employmentCost
        this.$emit('update:complianceData', this.internalComplianceData)
      },
    },
    errorMessage() {
      return FormValidation.containsErrorMessage(this.errors)
    },
  },
  methods: {
    async onSave() {
      this.loading = true
      this.showError = false
      this.showSuccess = false

      const data = JSON.stringify({
        employment_cost: this.employmentCost,
      })

      try {
        await this.$apollo.mutate({
          mutation: UpdateComplianceMutation,
          variables: {
            input: {
              name: 'EMPLOYMENT_COST',
              data,
            },
          },
        })

        this.showSuccess = true
      } catch ({ graphQLErrors }) {
        this.errors = graphQLErrors
        this.showError = true
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
